import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    DatePicker,
    Spin,
    Layout,
    Form,
    Button,
    Space,
} from "antd";
import { ProfileOutlined, FileExcelOutlined, FolderOpenOutlined } from "@ant-design/icons";
import moment from "moment";
import { Table } from "ant-table-extensions";
import { errorN, successN } from "../../general";
import { navigate } from "hookrouter";
import { readCardLog, parseWindowsCardLog } from "../Bookings/card_log";

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Text } = Typography;

// Same as Windows frmList_AccessCard: cards issued between Start and To.
// Shows the cards issued from this browser, plus any Windows AccessCard_M_YYYY.txt files
// opened with "Open Windows Log" (found in C:\Users\<user>\RatriLogs on the front desk PC).
function AccessCardLog() {
    const [form] = Form.useForm();
    const [data, setData] = useState([]);
    const [range, setRange] = useState("");
    const [windowsRows, setWindowsRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const fileInput = useRef(null);

    async function loadData(extraRows) {
        try {
            const values = await form.validateFields();

            const start = moment(values.dtDateRange[0]).startOf("day");
            const end = moment(values.dtDateRange[1]).endOf("day");

            setRange(`From ${start.format("DD-MM-YYYY")} to ${end.format("DD-MM-YYYY")}`);

            const browserRows = readCardLog().map(x => ({ ...x, source: "Web" }));
            const all = [...browserRows, ...(extraRows !== undefined ? extraRows : windowsRows)];

            const rows = all
                .filter(x => {
                    const dt = moment(x.date, "YYYY-MM-DD HH:mm:ss", true);
                    return dt.isValid() && dt.isSameOrAfter(start) && dt.isSameOrBefore(end);
                })
                .sort((a, b) => a.date.localeCompare(b.date))
                .map((x, i) => ({ ...x, key: i }));

            setData(rows);
        } catch (err) {
            if (err && err.errorFields) {
                return;
            }
            errorN(`Error Load Data: ${err}`);
        }
    }

    async function openWindowsLog(e) {
        // React 16 clears e.target after the first await, so keep the input to reset it later.
        const input = e.target;
        const files = Array.from(input.files || []);
        if (files.length === 0) {
            return;
        }

        setBusy(true);
        try {
            let rows = [];
            for (const f of files) {
                rows = rows.concat(parseWindowsCardLog(await f.text()));
            }
            setWindowsRows(rows);
            successN(`${rows.length} card(s) read from ${files.length} Windows log file(s).`);
            await loadData(rows);
        } catch (err) {
            errorN(`Error Open Log: ${err}`);
        } finally {
            setBusy(false);
            input.value = "";
        }
    }

    useEffect(() => {
        if (!localStorage.getItem("session")) {
            navigate("/");
        }
        form.setFieldsValue({
            dtDateRange: [moment().startOf("day"), moment().startOf("day").add(1, "days")],
        });
        loadData();
    }, []);

    const columns = [
        { width: 160, title: "Date", dataIndex: "date", key: "date", formatter: v => v },
        { width: 100, title: "Room No.", dataIndex: "roomno", key: "roomno", formatter: v => v },
        { width: 120, title: "Folio No.", dataIndex: "no", key: "no", formatter: v => v },
        { width: 160, title: "Check In", dataIndex: "start", key: "start", formatter: v => v },
        { width: 160, title: "Check Out", dataIndex: "end", key: "end", formatter: v => v },
        { width: 120, title: "Created By", dataIndex: "by", key: "by", formatter: v => v },
        { width: 90, title: "From", dataIndex: "source", key: "source", formatter: v => v },
    ];

    return (
        <>
            <div
                style={{
                    backgroundImage: `url("/header-bg7.png")`,
                    padding: "15px",
                    fontSize: "18px",
                }}
            >
                <ProfileOutlined /> &nbsp; Access Card Log
            </div>

            <Layout>
                <Spin spinning={busy}>
                    <Content style={{ backgroundColor: "white" }}>
                        <Form form={form} layout="vertical">
                            <div style={{ padding: 20, height: "73vh", overflow: "auto" }}>
                                <Space>
                                    <Form.Item
                                        label="Start - To"
                                        name="dtDateRange"
                                        rules={[{ required: true, message: "Dates missing!" }]}
                                    >
                                        <RangePicker format="DD-MM-YYYY" />
                                    </Form.Item>

                                    <Button type="primary" onClick={() => loadData()}>
                                        Load
                                    </Button>

                                    <Button icon={<FolderOpenOutlined />} onClick={() => fileInput.current && fileInput.current.click()}>
                                        Open Windows Log
                                    </Button>
                                    <input
                                        ref={fileInput}
                                        type="file"
                                        accept=".txt"
                                        multiple
                                        style={{ display: "none" }}
                                        onChange={openWindowsLog}
                                    />
                                </Space>

                                <div><Text type="secondary">{range}</Text></div>
                                <div>
                                    <Text type="secondary">
                                        Web rows are cards issued from this browser. Windows rows come from the RatriLogs files you open.
                                    </Text>
                                </div>

                                <Table
                                    columns={columns}
                                    dataSource={data}
                                    exportable
                                    exportableProps={{
                                        showColumnPicker: true,
                                        fileName: "AccessCard-Log",
                                        btnProps: {
                                            type: "primary",
                                            icon: <FileExcelOutlined />,
                                        },
                                    }}
                                    bordered
                                    size="small"
                                    scroll={{ x: "max-content", y: 360 }}
                                    pagination={false}
                                    footer={pageData => <Text strong>Total Cards: {pageData.length}</Text>}
                                />
                            </div>
                        </Form>
                    </Content>
                </Spin>
            </Layout>
        </>
    );
}

export default AccessCardLog;
